import Cookies from "js-cookie";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/**
 * The preference is stored per device in a cookie. The inline script in
 * root.tsx reads the same cookie before first paint to avoid a flash of the
 * wrong theme — keep the cookie name and the class/meta toggling in sync
 * with that script.
 */
export const THEME_COOKIE_NAME = "moneta_theme";
const THEME_COOKIE_EXPIRES_DAYS = 365;

export const THEME_COLOR_LIGHT = "#ffffff";
export const THEME_COLOR_DARK = "#161616";

export const isThemePreference = (value: unknown): value is ThemePreference =>
  value === "light" || value === "dark" || value === "system";

const getStoredThemePreference = (): ThemePreference => {
  const value = Cookies.get(THEME_COOKIE_NAME);
  return isThemePreference(value) ? value : "system";
};

const resolveTheme = (preference: ThemePreference): ResolvedTheme => {
  if (preference === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return preference;
};

const applyTheme = (preference: ThemePreference): ResolvedTheme => {
  const resolved = resolveTheme(preference);
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", resolved === "dark" ? THEME_COLOR_DARK : THEME_COLOR_LIGHT);
  return resolved;
};

type ThemeContextValue = {
  /** The stored per-device preference. */
  theme: ThemePreference;
  /** What is currently rendered, with "system" resolved. */
  resolvedTheme: ResolvedTheme;
  setTheme: (preference: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<ThemePreference>(() =>
    typeof document === "undefined" ? "system" : getStoredThemePreference(),
  );
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    typeof document === "undefined" ? "light" : resolveTheme(getStoredThemePreference()),
  );

  const setTheme = useCallback((preference: ThemePreference) => {
    Cookies.set(THEME_COOKIE_NAME, preference, {
      expires: THEME_COOKIE_EXPIRES_DAYS,
      path: "/",
      sameSite: "lax",
    });
    setThemeState(preference);
    setResolvedTheme(applyTheme(preference));
  }, []);

  // Converge the DOM with provider state on mount: the inline script and this
  // provider parse the cookie independently and could disagree (e.g. an
  // unrecognized cookie value falls back to "system" here).
  useEffect(() => {
    setResolvedTheme(applyTheme(theme));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Follow OS-level appearance changes while in "system" mode
  useEffect(() => {
    if (theme !== "system") {
      return;
    }
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setResolvedTheme(applyTheme("system"));
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [theme]);

  const value = useMemo(() => ({ theme, resolvedTheme, setTheme }), [theme, resolvedTheme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
